import { FC, useMemo } from "react";
import { GridLegacy as Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { usePreferences } from "../../utility/usePreferences";
import { capitalise } from "../../utility/utils";
import { Card, CardContent } from "./common/card";
import { IChartProps } from "./common/types";
import { useUserParams } from "../../context/user-params-context";
import { ChartInfoTooltip } from "./common/card-info-tooltip";
import { useChartData } from "../../hooks/useChartData";
import { useReleaseManagement, ReleaseFlag } from "@/utility/feature-management";
import { resolveChartTitle } from "./common/resolve-chart-title";

interface IQueryResult {
  value: string | number;
  fontColor?: string;
  bgColor?: string;
}

function isQueryResult(obj: unknown): obj is IQueryResult {
  if (obj == null || typeof obj !== "object") {
    return false;
  }
  const rec = obj as Record<string, unknown>;
  if (!("value" in rec)) {
    return false;
  }
  if (rec.fontColor != null && typeof rec.fontColor !== "string") {
    return false;
  }
  if (rec.bgColor != null && typeof rec.bgColor !== "string") {
    return false;
  }
  return true;
}

// TODO: remove once releaseRefactorChartTitle is fully rolled out.
enum TitleFormat {
  DISTANCE_PER_HOUR = "@preferences.distancePerHour",
  DISTANCE_UNIT = "@preferences.distanceUnit",
}

interface ITextChartOptions {
  /** Legacy title formatting */
  title?: { prefix?: TitleFormat; suffix?: TitleFormat };
  description?: string;
}

function isTextChartTitleOptions(
  obj: unknown
): obj is ITextChartOptions["title"] {
  if (!obj || typeof obj !== "object") {
    return false;
  }
  const rec = obj as Record<string, unknown>;
  const valid = (v: unknown) =>
    typeof v === "string" &&
    Object.values<string>(TitleFormat).includes(v as string);
  if ("suffix" in rec && rec.suffix != null && !valid(rec.suffix)) {
    return false;
  }
  if ("prefix" in rec && rec.prefix != null && !valid(rec.prefix)) {
    return false;
  }
  return true;
}

function isTextChartOptions(obj: unknown): obj is ITextChartOptions {
  if (!obj || typeof obj !== "object") {
    return false;
  }
  const rec = obj as Record<string, unknown>;
  if ("title" in rec && rec.title != null && !(isTextChartTitleOptions as any)(rec.title)) {
    return false;
  }
  return true;
}

export const TextChart: FC<IChartProps> = ({ uuid }) => {
  const { t } = useTranslation();
  const { parameterTypes, userParams } = useUserParams();
  const { distancePerHour, distanceUnitText, distanceUnitSingular } = usePreferences();

  const releaseRefactorChartTitle = useReleaseManagement(
    ReleaseFlag.releaseRefactorChartTitle
  );

  const { data, isLoading } = useChartData<IQueryResult[], ITextChartOptions>(
    uuid,
    parameterTypes,
    userParams,
    {
      validateOptions: isTextChartOptions,
      validateData: (d): d is IQueryResult[] =>
        Array.isArray(d) && d.length > 0 && isQueryResult(d[0]),
    }
  );

  const chartOptions = data?.options;
  const chartData =
    data?.data && data.data.length > 0 ? data.data[0] : undefined;
  const chartName = data?.name;
  const chartTranslationKey = data?.translationKey ?? undefined;

  // Build the ORIGINAL legacy title (prefix/suffix) so our default matches prior behaviour
  const legacyBaseTitle = useMemo(() => {
    if (!chartName) return undefined;
    let formatted = chartName;
    const legacy = chartOptions?.title;
    if (legacy?.suffix === TitleFormat.DISTANCE_PER_HOUR) {
      formatted = `${formatted} (${distancePerHour})`;
    }
    if (legacy?.suffix === TitleFormat.DISTANCE_UNIT) {
      formatted = `${formatted} ${capitalise(distanceUnitText)}`;
    }
    if (legacy?.prefix === TitleFormat.DISTANCE_PER_HOUR) {
      formatted = `(${distancePerHour}) ${formatted}`;
    }
    if (legacy?.prefix === TitleFormat.DISTANCE_UNIT) {
      formatted = `${capitalise(distanceUnitText)} (${formatted})`;
    }
    return formatted;
  }, [
    chartName,
    chartOptions?.title?.prefix,
    chartOptions?.title?.suffix,
    distancePerHour,
    distanceUnitText,
  ]);

  const computedTitle = useMemo(() => {
    if (!chartName) return "";
    return resolveChartTitle({
      t,
      name: chartName ?? undefined,
      translationKey: chartTranslationKey,
      featureEnabled: releaseRefactorChartTitle,
      // Allow translations to inject dynamic units/time formatting if needed.
      i18nParams: {
        distancePerHour,
        distanceUnit: capitalise(distanceUnitText),
        distanceUnitSingular: capitalise(distanceUnitSingular),
        timeFormat: "hh:mm",
      },
      // IMPORTANT: defaultText is the fully formatted legacy title.
      defaultText: legacyBaseTitle ?? chartName ?? undefined,
    });
  }, [
    t,
    chartName,
    chartTranslationKey,
    releaseRefactorChartTitle,
    legacyBaseTitle,
    distancePerHour,
    distanceUnitText,
    distanceUnitSingular,
  ]);

  return (
    <Grid item xs={4} md={3} lg={2}>
      <Card
        style={{
          backgroundColor: chartData?.bgColor || "hsl(var(--card))",
          animation: isLoading ? "pulse 2s linear infinite" : undefined,
          minHeight: 300,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        {chartOptions?.description && (
          <ChartInfoTooltip description={chartOptions.description} />
        )}
        <CardContent>
          <Typography
            variant="body1"
            style={{
              fontWeight: "bold",
              fontSize: "56px",
              margin: "24px 0",
              overflow: "hidden",
              color: chartData?.fontColor || "initial",
            }}
          >
            {isLoading ? 0 : chartData?.value ?? 0}
          </Typography>
          <Typography
            variant="subtitle1"
            color={chartData?.fontColor || "initial"}
          >
            {isLoading ? t("general.loading") : computedTitle}
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default TextChart;
